import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, ExternalLink, ThumbsUp, ThumbsDown, Star, MessageSquare, Plus, BookOpen, Sparkles, Zap, Target, Copy, Check, ChevronDown, ChevronUp, Lightbulb, Link2, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import EditToolModal from '@/components/EditToolModal';
import VictoryCelebration from '@/components/VictoryCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LEVEL_COLORS = {
    iniciante: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/30' },
    intermediario: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/30' },
    avancado: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' }
};

const LEVEL_LABELS = { iniciante: 'Iniciante', intermediario: 'Intermediário', avancado: 'Avançado' };

const VICTORY_TYPES = [
    { id: "first_doc_ai", label: "📄 Criei meu primeiro documento com ajuda do LLM" },
    { id: "deep_research", label: "🔎 Fiz minha primeira pesquisa profunda" },
    { id: "image_gen", label: "🎨 Criei imagens" },
    { id: "video_gen", label: "🎥 Criei vídeos" },
    { id: "presentation_gen", label: "📊 Criei Apresentações" },
    { id: "site_gen", label: "🌐 Criei um site" },
    { id: "automation_gen", label: "⚡ Criei uma automação" },
    { id: "personal_agent", label: "🤖 Criei um agente pessoal" },
    { id: "api_usage", label: "🔌 Usei APIs" },
    { id: "full_app", label: "🚀 Criei uma aplicação completa" },
    { id: "multi_agent", label: "🧠 Criei um projeto multi agentes" },
    { id: "biz_ai_plan", label: "💼 Fiz um plano de AI para meu negócio" },
    { id: "ai_first_area", label: "🏢 Implementei AI First na minha área" },
    { id: "ai_first_biz", label: "🏆 Implementei AI First no meu negócio" },
    // New Achievements
    { id: "prompt_architect", label: "⌨️ Arquiteto de Prompts" },
    { id: "data_weaver", label: "🕸️ Data Weaver" },
    { id: "tool_master", label: "🛠️ Mestre das Ferramentas" },
    { id: "efficiency_expert", label: "⏱️ Expert em Eficiência" },
    { id: "ai_strategist", label: "🧭 Estrategista AI" }
];

export default function ToolDetail({ user }) {
    const { toolId } = useParams();
    const navigate = useNavigate();
    const [tool, setTool] = useState(null);
    const [loading, setLoading] = useState(true);
    const [ratings, setRatings] = useState({ use_frequency: 0, liked: 0, know_how: 0 });
    const [expandedCase, setExpandedCase] = useState(0);
    const [showPromptTips, setShowPromptTips] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [showTipForm, setShowTipForm] = useState(false);
    const [newTip, setNewTip] = useState({ text: '', url: '' });
    const [evidenceLink, setEvidenceLink] = useState('');
    const [evidenceFile, setEvidenceFile] = useState(null);
    const [evidenceCategory, setEvidenceCategory] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [unlockedAchievement, setUnlockedAchievement] = useState(null);

    useEffect(() => {
        const fetchTool = async () => {
            try {
                const [toolRes, ratingRes] = await Promise.all([
                    axios.get(`${API}/ai-tools/${toolId}`, { withCredentials: true }),
                    axios.get(`${API}/ai-tools/${toolId}/rating`, { withCredentials: true })
                ]);
                setTool(toolRes.data);
                setRatings({
                    use_frequency: ratingRes.data.use_frequency || 0,
                    liked: ratingRes.data.liked || 0,
                    know_how: ratingRes.data.know_how || 0
                });
            } catch (error) {
                console.error('Error fetching tool:', error);
                toast.error('Ferramenta não encontrada');
                navigate('/ai-tools');
            } finally {
                setLoading(false);
            }
        };
        fetchTool();
    }, [toolId, navigate]);

    const handleRatingSubmit = async () => {
        setIsSubmitting(true);
        try {
            let finalEvidenceUrl = null;
            let finalEvidenceType = null;

            // 1. Upload File if exists
            if (evidenceFile) {
                const formData = new FormData();
                formData.append('file', evidenceFile);

                try {
                    const uploadRes = await axios.post(`${API}/uploads/evidence`, formData, {
                        withCredentials: true,
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    finalEvidenceUrl = uploadRes.data.url;
                    finalEvidenceType = 'file';
                } catch (uploadErr) {
                    console.error('Upload failed:', uploadErr);
                    toast.error('Erro ao fazer upload da evidência');
                    setIsSubmitting(false);
                    return;
                }
            } else if (evidenceLink) {
                finalEvidenceUrl = evidenceLink;
                finalEvidenceType = 'link';
            }

            // 2. Submit Rating
            const payload = {
                ...ratings,
                evidence_url: finalEvidenceUrl,
                evidence_type: finalEvidenceType,
                evidence_category: evidenceCategory || null
            };

            const response = await axios.post(
                `${API}/ai-tools/${toolId}/rate`,
                payload,
                { withCredentials: true, params: payload }
            );

            if (response.data.unlocked_achievement) {
                setUnlockedAchievement(response.data.unlocked_achievement);
            }
            toast.success('Avaliação salva com sucesso!');
        } catch (error) {
            console.error('Rating error:', error);
            toast.error('Erro ao salvar avaliação');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        toast.success('Copiado!');
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const handleAddTip = async () => {
        if (!newTip.text.trim()) return;
        try {
            const response = await axios.post(
                `${API}/ai-tools/${toolId}/tips`,
                newTip,
                { withCredentials: true }
            );
            setTool(prev => ({
                ...prev,
                user_tips: [...(prev.user_tips || []), response.data.tip]
            }));
            setNewTip({ text: '', url: '' });
            setShowTipForm(false);
            toast.success('Dica adicionada!');
        } catch (error) {
            toast.error('Erro ao adicionar dica');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen">
                <Navigation user={user} />
                <div className="flex items-center justify-center py-32">
                    <div className="text-center">
                        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
                        <p className="mt-4 text-sm text-muted-foreground">Carregando ferramenta...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!tool) return null;

    const levelColor = LEVEL_COLORS[tool.level] || LEVEL_COLORS.iniciante;

    // Check permission: admin or owner
    const canEdit = user && (user.role === 'admin' || user.role === 'super_admin' || (tool.submitted_by && tool.submitted_by === user.user_id));

    return (
        <div className="min-h-screen">
            <Navigation user={user} />

            <main className="container mx-auto px-4 md:px-8 py-8 max-w-5xl">
                {/* Back button */}
                <button
                    onClick={() => navigate('/ai-tools')}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8 group"
                    data-testid="back-to-tools"
                >
                    <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                    <span className="uppercase tracking-wide font-medium">Voltar às Ferramentas</span>
                </button>

                {/* Header */}
                <div className="flex flex-col md:flex-row items-start gap-6 mb-10 pb-8 border-b border-border">
                    <div className="flex items-center gap-4 flex-1">
                        {tool.logo_url && (
                            <img
                                src={tool.logo_url}
                                alt={tool.name}
                                className="h-16 w-16 object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        )}
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-none mb-3" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="tool-detail-title">
                                    {tool.name}
                                </h1>
                                {canEdit && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-8 text-xs uppercase tracking-wide"
                                        onClick={() => setShowEditModal(true)}
                                    >
                                        Editar
                                    </Button>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {tool.is_free && (
                                    <span className="text-xs px-3 py-1 bg-green-500/10 text-green-400 border border-green-500/30 rounded-none uppercase tracking-wide font-medium">
                                        {tool.pricing_details || 'Free'}
                                    </span>
                                )}
                                {!tool.is_free && tool.pricing_details && (
                                    <span className="text-xs px-3 py-1 bg-orange-500/10 text-orange-400 border border-orange-500/30 rounded-none uppercase tracking-wide font-medium">
                                        {tool.pricing_details}
                                    </span>
                                )}
                                {tool.level && (
                                    <span className={`text-xs px-3 py-1 rounded-none uppercase tracking-wide font-medium border ${levelColor.bg} ${levelColor.text} ${levelColor.border}`}>
                                        {LEVEL_LABELS[tool.level] || tool.level}
                                    </span>
                                )}
                                <span className="text-xs px-3 py-1 bg-muted rounded-none uppercase tracking-wide font-medium">
                                    {tool.category}
                                </span>
                            </div>
                        </div>
                    </div>
                    {tool.url && (
                        <a
                            href={tool.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium tracking-wide uppercase text-xs hover:bg-primary/90 transition-colors rounded-none"
                            data-testid="tool-visit-btn"
                        >
                            Visitar Site <ExternalLink className="h-4 w-4" />
                        </a>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main content - 2 columns */}
                    <div className="lg:col-span-2 space-y-8">

                        {/* Sobre a Ferramenta */}
                        <Card className="p-6 rounded-none border border-border" data-testid="about-section">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-primary/10 rounded-none">
                                    <BookOpen className="h-5 w-5 text-primary" strokeWidth={1.5} />
                                </div>
                                <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Sobre a Ferramenta</h2>
                            </div>
                            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                                {tool.how_it_works || tool.description}
                            </p>
                        </Card>

                        {/* Destaques */}
                        {tool.highlights && tool.highlights.length > 0 && (
                            <Card className="p-6 rounded-none border border-border" data-testid="highlights-section">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-yellow-500/10 rounded-none">
                                        <Sparkles className="h-5 w-5 text-yellow-500" strokeWidth={1.5} />
                                    </div>
                                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Destaques</h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {tool.highlights.map((h, i) => (
                                        <div key={i} className="p-4 border border-border rounded-none bg-muted/30">
                                            <div className="flex items-start gap-2">
                                                <Zap className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                                                <span className="text-sm">{h}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {/* Casos de Uso */}
                        {tool.use_cases && tool.use_cases.length > 0 && (
                            <Card className="p-6 rounded-none border border-border" data-testid="usecases-section">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-blue-500/10 rounded-none">
                                        <Target className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                                    </div>
                                    <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Casos de Uso</h2>
                                </div>
                                <div className="space-y-3">
                                    {tool.use_cases.map((uc, i) => (
                                        <div key={i} className="border border-border rounded-none overflow-hidden">
                                            <button
                                                className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors text-left"
                                                onClick={() => setExpandedCase(expandedCase === i ? -1 : i)}
                                            >
                                                <span className="font-medium">Showcase: {uc.title}</span>
                                                {expandedCase === i ? (
                                                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                                ) : (
                                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                )}
                                            </button>

                                            {expandedCase === i && (
                                                <div className="px-4 pb-4 space-y-4">
                                                    {/* Prompt Inadequado */}
                                                    {uc.bad_prompt && (
                                                        <div className="rounded-none overflow-hidden">
                                                            <div className="flex items-center gap-2 px-4 py-2 bg-red-500/10 border-l-4 border-red-500">
                                                                <span className="text-red-400 text-sm font-medium">✕ Prompt Inadequado</span>
                                                            </div>
                                                            <div className="p-4 bg-muted/50 border border-border border-t-0">
                                                                <p className="text-sm text-muted-foreground italic">"{uc.bad_prompt}"</p>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Prompt Eficiente */}
                                                    {uc.good_prompt && (
                                                        <div className="rounded-none overflow-hidden">
                                                            <div className="flex items-center justify-between px-4 py-2 bg-green-500/10 border-l-4 border-green-500">
                                                                <span className="text-green-400 text-sm font-medium">✓ Prompt eficiente / Instruções</span>
                                                                <button
                                                                    onClick={() => handleCopy(uc.good_prompt, `uc-${i}`)}
                                                                    className="flex items-center gap-1 px-3 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs rounded-none transition-colors"
                                                                >
                                                                    {copiedIndex === `uc-${i}` ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                                                    {copiedIndex === `uc-${i}` ? 'Copiado' : 'Copiar'}
                                                                </button>
                                                            </div>
                                                            <div className="p-4 bg-muted/50 border border-border border-t-0">
                                                                <p className="text-sm text-foreground">"{uc.good_prompt}"</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {/* Dicas de Prompt */}
                        {tool.prompt_tips && tool.prompt_tips.length > 0 && (
                            <Card className="p-6 rounded-none border border-border" data-testid="prompt-tips-section">
                                <button
                                    className="w-full flex items-center justify-between"
                                    onClick={() => setShowPromptTips(!showPromptTips)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-purple-500/10 rounded-none">
                                            <Lightbulb className="h-5 w-5 text-purple-500" strokeWidth={1.5} />
                                        </div>
                                        <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Dicas de Formato do Prompt</h2>
                                    </div>
                                    {showPromptTips ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                                </button>
                                {showPromptTips && (
                                    <ul className="mt-4 space-y-3">
                                        {tool.prompt_tips.map((tip, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm">
                                                <span className="text-primary mt-0.5">💡</span>
                                                <span className="text-muted-foreground">{tip}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Card>
                        )}

                        {/* Prós & Contras */}
                        {((tool.pros && tool.pros.length > 0) || (tool.cons && tool.cons.length > 0)) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {tool.pros && tool.pros.length > 0 && (
                                    <Card className="p-6 rounded-none border border-border" data-testid="pros-section">
                                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                            <ThumbsUp className="h-5 w-5 text-green-500" /> Pontos Positivos
                                        </h3>
                                        <ul className="space-y-2">
                                            {tool.pros.map((p, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm">
                                                    <span className="text-green-500 mt-0.5">+</span>
                                                    <span>{p}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </Card>
                                )}
                                {tool.cons && tool.cons.length > 0 && (
                                    <Card className="p-6 rounded-none border border-border" data-testid="cons-section">
                                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                            <ThumbsDown className="h-5 w-5 text-red-500" /> Pontos Negativos
                                        </h3>
                                        <ul className="space-y-2">
                                            {tool.cons.map((c, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm">
                                                    <span className="text-red-500 mt-0.5">−</span>
                                                    <span>{c}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </Card>
                                )}
                            </div>
                        )}

                        {/* Features */}
                        {tool.main_features && tool.main_features.length > 0 && (
                            <Card className="p-6 rounded-none border border-border" data-testid="features-section">
                                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Principais Features</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {tool.main_features.map((f, i) => (
                                        <div key={i} className="flex items-start gap-2 text-sm p-2">
                                            <span className="text-primary mt-0.5">•</span>
                                            <span>{f}</span>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}
                    </div>

                    {/* Sidebar - 1 column */}
                    <div className="space-y-6">

                        {/* Rating Card */}
                        <Card className="p-6 rounded-none border-2 border-primary" data-testid="rating-card">
                            <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                <Star className="h-5 w-5 text-primary inline mr-2" />
                                Sua Avaliação
                            </h3>
                            {tool.avg_ratings && tool.avg_ratings.total > 0 && (
                                <p className="text-xs text-muted-foreground mb-4">
                                    {tool.avg_ratings.total} avaliação(ões) • Média geral: {((tool.avg_ratings.use_frequency + tool.avg_ratings.liked + tool.avg_ratings.know_how) / 3).toFixed(1)}/5
                                </p>
                            )}

                            <div className="space-y-5">
                                <div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <label className="font-medium">Uso Muito</label>
                                        <span className="text-muted-foreground">{ratings.use_frequency}/5</span>
                                    </div>
                                    <Slider
                                        value={[ratings.use_frequency]}
                                        onValueChange={([v]) => setRatings(r => ({ ...r, use_frequency: v }))}
                                        max={5} step={1}
                                        data-testid="rating-use"
                                    />
                                </div>
                                <div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <label className="font-medium">Gostei</label>
                                        <span className="text-muted-foreground">{ratings.liked}/5</span>
                                    </div>
                                    <Slider
                                        value={[ratings.liked]}
                                        onValueChange={([v]) => setRatings(r => ({ ...r, liked: v }))}
                                        max={5} step={1}
                                        data-testid="rating-liked"
                                    />
                                </div>
                                <div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <label className="font-medium">Sei usar / Explorei</label>
                                        <span className="text-muted-foreground">{ratings.know_how}/5</span>
                                    </div>
                                    <Slider
                                        value={[ratings.know_how]}
                                        onValueChange={([v]) => setRatings(r => ({ ...r, know_how: v }))}
                                        max={5} step={1}
                                        data-testid="rating-know"
                                    />
                                </div>

                                {/* Prova de Uso UI */}
                                <div className="pt-4 border-t border-border">
                                    <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                                        <Link2 className="h-4 w-4" /> Prova de Uso (Opcional)
                                    </h4>

                                    <div className="space-y-3">
                                        <Input
                                            placeholder="Cole um link (Projeto, Chat, Site...)"
                                            value={evidenceLink}
                                            onChange={(e) => setEvidenceLink(e.target.value)}
                                            className="rounded-none text-sm"
                                            disabled={!!evidenceFile}
                                        />

                                        <select
                                            className="flex h-10 w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            value={evidenceCategory}
                                            onChange={(e) => setEvidenceCategory(e.target.value)}
                                        >
                                            <option value="">Selecione a Conquista (Opcional)...</option>
                                            {VICTORY_TYPES.map(type => (
                                                <option key={type.id} value={type.id}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>

                                        <div className="relative">
                                            <input
                                                type="file"
                                                id="evidence-upload"
                                                className="hidden"
                                                onChange={(e) => setEvidenceFile(e.target.files[0])}
                                                accept=".jpg,.jpeg,.png,.pdf,.webp"
                                                disabled={!!evidenceLink}
                                            />
                                            <label
                                                htmlFor="evidence-upload"
                                                className={`flex items-center justify-center gap-2 w-full py-2 border border-dashed text-sm cursor-pointer transition-colors ${evidenceFile ? 'bg-primary/10 border-primary text-primary' : (evidenceLink ? 'opacity-50 cursor-not-allowed bg-muted' : 'border-border hover:bg-muted/50')}`}
                                            >
                                                <Upload className="h-4 w-4" />
                                                {evidenceFile ? evidenceFile.name : "Ou anexe um arquivo (Print/PDF)"}
                                            </label>
                                            {evidenceFile && (
                                                <button
                                                    onClick={(e) => { e.preventDefault(); setEvidenceFile(null); }}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-red-100 text-red-500 rounded-full"
                                                >
                                                    <span className="text-xs font-bold">✕</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Button
                                onClick={handleRatingSubmit}
                                className="w-full mt-6 rounded-none font-medium tracking-wide uppercase text-xs"
                                data-testid="save-rating"
                                disabled={isSubmitting} // Need to add isSubmitting state
                            >
                                {isSubmitting ? 'Salvando...' : 'Salvar Avaliação'}
                            </Button>
                        </Card>

                        {/* User Tips */}
                        <Card className="p-6 rounded-none border border-border" data-testid="user-tips-section">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <MessageSquare className="h-5 w-5 inline mr-2" />
                                    Atualizações e Dicas
                                </h3>
                                <button
                                    onClick={() => setShowTipForm(!showTipForm)}
                                    className="flex items-center gap-1 px-3 py-1 text-xs font-medium uppercase tracking-wide border border-border hover:border-primary/50 transition-colors rounded-none"
                                >
                                    <Plus className="h-3 w-3" /> Adicionar
                                </button>
                            </div>

                            {showTipForm && (
                                <div className="mb-4 p-4 border border-border rounded-none space-y-3">
                                    <Input
                                        value={newTip.text}
                                        onChange={e => setNewTip(t => ({ ...t, text: e.target.value }))}
                                        placeholder="Escreva sua dica..."
                                        className="rounded-none text-sm"
                                        data-testid="tip-text-input"
                                    />
                                    <Input
                                        value={newTip.url}
                                        onChange={e => setNewTip(t => ({ ...t, url: e.target.value }))}
                                        placeholder="Link útil (opcional)"
                                        className="rounded-none text-sm"
                                        data-testid="tip-url-input"
                                    />
                                    <div className="flex gap-2">
                                        <Button size="sm" onClick={handleAddTip} className="rounded-none text-xs uppercase tracking-wide flex-1" data-testid="submit-tip">
                                            Enviar
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => setShowTipForm(false)} className="rounded-none text-xs uppercase tracking-wide">
                                            Cancelar
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {tool.user_tips && tool.user_tips.length > 0 ? (
                                <div className="space-y-3">
                                    {tool.user_tips.map((tip, i) => (
                                        <div key={i} className="p-3 border border-border rounded-none bg-muted/20">
                                            <p className="text-sm mb-1">{tip.text}</p>
                                            {tip.url && (
                                                <a
                                                    href={tip.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-primary hover:underline flex items-center gap-1"
                                                >
                                                    <Link2 className="h-3 w-3" /> {tip.url.length > 40 ? tip.url.substring(0, 40) + '...' : tip.url}
                                                </a>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-2">
                                                por {tip.user_name} • {new Date(tip.created_at).toLocaleDateString('pt-BR')}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-muted-foreground">
                                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                    <p className="text-sm">Nenhuma dica adicionada ainda.</p>
                                    <p className="text-xs">Adicione links úteis e dicas!</p>
                                </div>
                            )}
                        </Card>

                        {/* Tags */}
                        {tool.tags && tool.tags.length > 0 && (
                            <Card className="p-6 rounded-none border border-border">
                                <h3 className="text-sm font-medium uppercase tracking-wide mb-3 text-muted-foreground">Tags</h3>
                                <div className="flex flex-wrap gap-2">
                                    {tool.tags.map((tag, i) => (
                                        <span key={i} className="text-xs px-2 py-1 bg-muted rounded-none">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </Card>
                        )}
                    </div>
                </div>

                {/* Edit Modal */}
                {showEditModal && (
                    <EditToolModal
                        tool={tool}
                        onClose={() => setShowEditModal(false)}
                        onUpdate={(updatedTool) => {
                            setTool(updatedTool);
                            setShowEditModal(false);
                        }}
                        onDelete={() => {
                            setShowEditModal(false);
                            navigate('/ai-tools');
                        }}
                    />
                )}
            </main>

            <VictoryCelebration
                achievement={unlockedAchievement}
                onClose={() => setUnlockedAchievement(null)}
            />
        </div>
    );
}
